import { activate } from './AwsUi';
import logger from '../../utils/src/logger';
import { createElement, render } from './mithril';
import { isPrimitiveRegion } from './BasePrototype/RegionHandling';
function updateProperty(component, propertyName, propertyValue) {
    component[propertyName] = propertyValue;
}
function removeRegion(component, regionName) {
    if (component.__mithrilRenderRegions[regionName]) {
        // mithril way of saying ReactDOM.unmountComponentAtNode()
        render(component.__mithrilRenderRegions[regionName], null);
        delete component.__mithrilRenderRegions[regionName];
    }
    component.removeRegion(regionName);
}
function updateRegion(component, regionName, regionValue) {
    if (regionValue === null || regionValue === undefined) {
        removeRegion(component, regionName);
        return;
    }
    if (isPrimitiveRegion(regionValue) || regionValue instanceof Node) {
        removeRegion(component, regionName);
        component.setRegion(regionName, regionValue);
        return;
    }
    var regionNode = component.__mithrilRenderRegions[regionName];
    if (!regionNode) {
        regionNode = document.createElement('span');
        component.__mithrilRenderRegions[regionName] = regionNode;
        component.setRegion(regionName, regionNode);
    }
    render(regionNode, regionValue);
}
function removeEvent(component, eventName) {
    var storedEventHandler = component.__mithrilRenderEvents[eventName];
    if (storedEventHandler) {
        component.node.removeEventListener("awsui:" + eventName, storedEventHandler.wrappedCallback);
        delete component.__mithrilRenderEvents[eventName];
    }
}
function updateEvent(component, eventName, eventCallback) {
    var storedEventHandler = component.__mithrilRenderEvents[eventName];
    if (storedEventHandler) {
        if (storedEventHandler.originalCallback === eventCallback) {
            return;
        }
        removeEvent(component, eventName);
    }
    if (eventCallback === undefined || eventCallback === null) {
        return;
    }
    if (typeof eventCallback !== 'function') {
        logger.error("The event handler you specified for " + eventName + " is invalid,\n      it can be either a function, null or undefined.");
        return;
    }
    var eventHandler = {
        originalCallback: eventCallback,
        wrappedCallback: function (event) {
            // Only apply if the event was triggered by the same element that
            // defined the handler.
            if (event.target === component.node) {
                eventCallback(event);
            }
        }
    };
    component.node.addEventListener("awsui:" + eventName, eventHandler.wrappedCallback);
    component.__mithrilRenderEvents[eventName] = eventHandler;
}
function removeMissingAttributes(definition, attributes, removingFunction) {
    for (var key in definition) {
        var notExists = !(key in attributes);
        var isPublic = !/^__/.test(key);
        if (notExists && isPublic) {
            removingFunction(key);
        }
    }
}
function findClosestParentComponent(element) {
    var parentNode = element.parentNode;
    while (parentNode && !parentNode.component) {
        parentNode = parentNode.parentNode;
    }
    if (!parentNode) {
        logger.error('Embedded component can only exist in a context of other component.');
        return;
    }
    return parentNode.component;
}
function componentDidUpdate(component, attributes) {
    component.__batchUpdate(function () {
        for (var attribute in attributes) {
            var value = attributes[attribute];
            if (attribute in (component.__events || {})) {
                updateEvent(component, attribute, value);
            }
            else if (attribute in (component.__regionDefinition || {})) {
                updateRegion(component, attribute, value);
            }
            else {
                updateProperty(component, attribute, value);
            }
        }
        removeMissingAttributes(component.__propertiesDefinition, attributes, function (def) { return component.__setToDefault(def); });
        removeMissingAttributes(component.__regionDefinition, attributes, function (region) { return component.__setToDefault(region); });
        removeMissingAttributes(component.__events, attributes, function (event) { return updateEvent(component, event, null); });
    });
}
function componentDidMount(element, ComponentClass, attributes) {
    activate(element, ComponentClass);
    var component = element.component;
    component.__closestParentComponent = findClosestParentComponent(element);
    component.__mithrilRenderRegions = component.__mithrilRenderRegions || {};
    component.__mithrilRenderEvents = component.__mithrilRenderEvents || {};
    componentDidUpdate(component, attributes);
    component.__closestParentComponent.__addEmbeddedComponent(component);
}
function componentWillUnmount(component) {
    component.__closestParentComponent.__removeEmbeddedComponent(component);
    component.hasBeenDetached();
    for (var region in component.__regionDefinition) {
        removeRegion(component, region);
    }
    for (var event_1 in component.__events) {
        removeEvent(component, event_1);
    }
}
export function createComponent(ComponentClass, attributes) {
    if (attributes === void 0) { attributes = {}; }
    var awsUi = ComponentClass.awsUi;
    attributes = attributes || {};
    var mithrilAttributes = {
        lifecycleKey: awsUi.name,
        componentDidMount: function (element) { return componentDidMount(element, ComponentClass, attributes); },
        componentDidUpdate: function (element) { return componentDidUpdate(element.component, attributes); },
        componentWillUnmount: function (element) { return componentWillUnmount(element.component); }
    };
    for (var attribute in attributes) {
        if (attribute.match(/^data-|^(id|className|key)$/) && attributes[attribute]) {
            mithrilAttributes[attribute] = attributes[attribute];
        }
    }
    return createElement(awsUi.name, mithrilAttributes);
}
//# sourceMappingURL=embeddedComponents.js.map